import { useState, useEffect } from 'react';
import { Star, Quote, ChevronLeft, ChevronRight } from 'lucide-react';

const Testimonials = () => {
  const [currentIndex, setCurrentIndex] = useState(0);
  const [testimonials, setTestimonials] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isEnabled, setIsEnabled] = useState(true);

  useEffect(() => {
    fetchTestimonials();
  }, []);

  const fetchTestimonials = async () => {
    try {
      // First check if testimonials are enabled
      const settingsResponse = await fetch('/api/settings-public');
      if (settingsResponse.ok) {
        const settings = await settingsResponse.json();
        if (settings.testimonials_enabled === false) {
          setIsEnabled(false);
          setLoading(false);
          return; // Don't fetch testimonials if disabled
        }
      }

      const response = await fetch('/api/testimonials-public');
      if (response.ok) {
        const data = await response.json();
        if (data.length > 0) {
          setTestimonials(data);
        } else {
          // Only show defaults if enabled but no testimonials exist
          setTestimonials(defaultTestimonials);
        }
      } else {
        setTestimonials(defaultTestimonials);
      }
    } catch (error) {
      console.error('Testimonials fetch error:', error);
      setTestimonials(defaultTestimonials);
    } finally {
      setLoading(false);
    }
  };

  const defaultTestimonials = [
    {
      id: 1,
      name: "Ahmet Yılmaz",
      project: "Yenibosna Projesi",
      rating: 5,
      comment: "MİGA Yapı ile çalışmak gerçekten mükemmeldi. Projemiz zamanında teslim edildi ve kalite beklentilerimizin üzerindeydi. Profesyonel ekip ve detaylara gösterdikleri özen için teşekkür ederiz.",
      avatar: "/images/avatars/avatar1.jpg"
    },
    {
      id: 2,
      name: "Ayşe Demir",
      project: "GÜL Apartmanı",
      rating: 5,
      comment: "35 yıllık tecrübeleri gerçekten fark yarattı. Baştan sona şeffaf ve dürüst iletişim. Hayalimizdeki evi gerçeğe dönüştürdüler. Kesinlikle tavsiye ediyorum!",
      avatar: "/images/avatars/avatar2.jpg"
    },
    {
      id: 3,
      name: "Mehmet Kaya",
      project: "Serince Apartmanı",
      rating: 5,
      comment: "Kaliteli malzeme, titiz işçilik ve zamanında teslimat. MİGA Yapı'nın uzman kadrosu her aşamada yanımızdaydı. Çok memnun kaldık.",
      avatar: "/images/avatars/avatar3.jpg"
    }
  ];

  const handleNext = () => {
    setCurrentIndex((prev) => (prev + 1) % testimonials.length);
  };

  const handlePrevious = () => {
    setCurrentIndex((prev) => (prev - 1 + testimonials.length) % testimonials.length);
  };

  // Auto-advance carousel
  useEffect(() => {
    if (testimonials.length <= 1) return;
    
    const interval = setInterval(() => {
      handleNext();
    }, 5000); // Change every 5 seconds

    return () => clearInterval(interval);
  }, [testimonials.length, currentIndex]);

  if (loading) {
    return (
      <div className="py-20 bg-gradient-to-br from-gray-50 to-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <div className="animate-pulse">
            <div className="h-8 bg-gray-200 rounded w-64 mx-auto mb-4"></div>
            <div className="h-4 bg-gray-200 rounded w-96 mx-auto"></div>
          </div>
        </div>
      </div>
    );
  }

  // Hide section if disabled or no testimonials
  if (!isEnabled || testimonials.length === 0) return null;

  const current = testimonials[currentIndex];

  return (
    <section className="py-20 bg-gradient-to-br from-gray-50 to-white relative overflow-hidden">
      {/* Decorative Elements */}
      <div className="absolute top-10 left-10 opacity-10">
        <Quote className="h-32 w-32 text-[#1e4a7a]" />
      </div>
      <div className="absolute bottom-10 right-10 opacity-10">
        <Quote className="h-32 w-32 text-[#1e4a7a] transform rotate-180" />
      </div>

      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
        {/* Header */}
        <div className="text-center mb-12">
          <h2 className="heading-font text-4xl sm:text-5xl font-bold text-[#1e4a7a] mb-4">
            Müşterilerimiz Ne Diyor?
          </h2>
          <p className="text-lg text-gray-600">
            Projelerimizi tamamlayan müşterilerimizin deneyimleri
          </p>
        </div>

        {/* Testimonial Card */}
        <div className="bg-white rounded-3xl shadow-2xl p-8 md:p-12 relative">
          {/* Stars */}
          <div className="flex justify-center mb-6">
            {[...Array(5)].map((_, i) => (
              <Star
                key={i}
                className={`h-6 w-6 ${
                  i < current.rating
                    ? 'text-yellow-400 fill-yellow-400'
                    : 'text-gray-300'
                }`}
              />
            ))}
          </div>

          {/* Comment */}
          <p className="text-xl text-gray-700 text-center mb-8 italic leading-relaxed">
            "{current.comment}"
          </p>

          {/* Author */}
          <div className="flex flex-col items-center">
            <div className="w-16 h-16 bg-gradient-to-br from-[#1e4a7a] to-[#2563eb] rounded-full flex items-center justify-center text-white text-2xl font-bold mb-4">
              {current.name.charAt(0)}
            </div>
            <h4 className="font-bold text-lg text-gray-900">{current.name}</h4>
            <p className="text-sm text-gray-500">{current.project}</p>
          </div>

          {/* Navigation */}
          {testimonials.length > 1 && (
            <div className="flex items-center justify-center gap-4 mt-8">
              <button
                onClick={handlePrevious}
                className="p-2 rounded-full bg-gray-100 hover:bg-gray-200 transition-colors"
                aria-label="Önceki"
              >
                <ChevronLeft className="h-6 w-6 text-gray-700" />
              </button>

              {/* Dots */}
              <div className="flex gap-2">
                {testimonials.map((_, index) => (
                  <button
                    key={index}
                    onClick={() => setCurrentIndex(index)}
                    className={`h-2 rounded-full transition-all ${
                      index === currentIndex
                        ? 'w-8 bg-[#dc2626]'
                        : 'w-2 bg-gray-300 hover:bg-gray-400'
                    }`}
                    aria-label={`Go to testimonial ${index + 1}`}
                  />
                ))}
              </div>

              <button
                onClick={handleNext}
                className="p-2 rounded-full bg-gray-100 hover:bg-gray-200 transition-colors"
                aria-label="Sonraki"
              >
                <ChevronRight className="h-6 w-6 text-gray-700" />
              </button>
            </div>
          )}
        </div>

        {/* All Testimonials Count */}
        <div className="text-center mt-8 text-gray-600">
          <p className="text-sm">
            {testimonials.length} memnun müşterimizden biri
          </p>
        </div>
      </div>
    </section>
  );
};

export default Testimonials;
